import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ListChecks, WarningCircle, Clock, House, Scales, FileText, CaretRight } from "@phosphor-icons/react";
import { useHousehold } from "@/context/HouseholdContext";
import { useDemo } from "@/context/DemoContext";
import { entityRenewal, liabilityDue, assetDelinquent, ASSET_TYPE_LABELS } from "@/lib/api";

export default function Issues() {
  const navigate = useNavigate();
  const { fmtAsset } = useDemo();
  const { assets, liabilities, entities } = useHousehold();

  const issues = useMemo(() => {
    const out = [];
    // Asset-level: delinquent taxes, lease expiring, needs review
    (assets || []).forEach((a) => {
      if (assetDelinquent(a)) {
        out.push({ key: `tax-${a.id}`, icon: WarningCircle, tone: "destructive",
          title: `Property tax delinquent: ${a.name}`,
          detail: "Tax records show one or more delinquent years. Open the asset to review.",
          to: a.asset_type === "real_estate" ? `/properties/${a.id}` : `/assets/${a.id}` });
      }
      if (a.lease_alert) {
        out.push({ key: `lease-${a.id}`, icon: Clock, tone: "warning",
          title: `Lease expiring soon: ${a.name}`,
          detail: "A unit lease ends within 30 days (or has expired).",
          to: `/properties/${a.id}` });
      }
      if (a.review_status === "needs_review") {
        out.push({ key: `review-${a.id}`, icon: FileText, tone: "muted",
          title: `Needs review: ${a.name}`,
          detail: `${ASSET_TYPE_LABELS[a.asset_type] || a.asset_type} · current value ${fmtAsset(a.current_value)}`,
          to: a.asset_type === "real_estate" ? `/properties/${a.id}` : `/assets/${a.id}` });
      }
    });
    // Liability-level: due soon / past due
    (liabilities || []).forEach((l) => {
      const due = liabilityDue(l.due_date);
      if (due.due) {
        out.push({ key: `liab-${l.id}`, icon: Scales, tone: due.days < 0 ? "destructive" : "warning",
          title: `${l.name || "Liability"} ${due.days < 0 ? "past due" : "due soon"}`,
          detail: `${due.days < 0 ? `${-due.days} days late` : `${due.days} days`} · balance ${fmtAsset(l.current_balance)}`,
          to: "/liabilities" });
      }
    });
    // Entity-level: renewal flag
    (entities || []).forEach((e) => {
      const r = entityRenewal(e.renewal_date, e.type);
      if (r.due) {
        out.push({ key: `ent-${e.id}`, icon: House, tone: "warning",
          title: `${e.name}: ${r.missing ? "no renewal date tracked" : `renewal in ${r.days} days`}`,
          detail: r.missing ? "Add the annual renewal date to receive 30-day reminders." : "Annual filing or fee is approaching.",
          to: `/entities/${e.id}` });
      }
    });
    return out;
  }, [assets, liabilities, entities, fmtAsset]);

  const toneCls = (t) => ({
    destructive: "bg-[hsl(5_60%_94%)] text-[hsl(5_55%_40%)]",
    warning:     "bg-[hsl(35_70%_92%)] text-[hsl(28_60%_32%)]",
    muted:       "bg-[hsl(30_15%_92%)] text-[hsl(30_15%_36%)]",
  }[t] || "bg-muted text-muted-foreground");

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="flex items-start gap-4 pb-6 border-b border-border">
        <div className="h-12 w-12 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] flex items-center justify-center shadow-sm">
          <ListChecks size={22} weight="regular" />
        </div>
        <div>
          <p className="eyebrow">Operations</p>
          <h1 className="page-title text-[44px] sm:text-[52px] mt-1" data-testid="page-title">Issues</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl leading-relaxed">
            Everything that needs your attention across assets, liabilities, and entities.
          </p>
        </div>
      </div>

      {issues.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-16 text-center" data-testid="issues-empty">
          <ListChecks size={32} className="mx-auto opacity-30 mb-3" />
          <p className="font-serif text-xl">All clear</p>
          <p className="text-sm text-muted-foreground mt-1">No outstanding issues right now.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <ul className="divide-y divide-border">
            {issues.map((it) => (
              <li
                key={it.key}
                data-testid={`issue-${it.key}`}
                onClick={() => navigate(it.to)}
                className="px-5 py-4 flex items-center gap-4 cursor-pointer hover:bg-[hsl(36_30%_96%)] transition-colors"
              >
                <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${toneCls(it.tone)}`}>
                  <it.icon size={16} weight="fill" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{it.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{it.detail}</p>
                </div>
                <CaretRight size={14} className="text-muted-foreground shrink-0" />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
